import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { TASK_STATUSES, TASK_STATUS_LABELS } from "@/lib/constants";

const TimerContext = createContext(null);

function computeElapsedSeconds(timer) {
  if (!timer) return 0;
  const acc = timer.accumulated_seconds || 0;
  if (timer.status !== "RUNNING") return acc;
  const anchor = timer.last_resumed_at || timer.started_at;
  if (!anchor) return acc;
  const started = new Date(anchor).getTime();
  const now = Date.now();
  return acc + Math.max(0, Math.floor((now - started) / 1000));
}

export function formatDuration(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

export function TimerProvider({ children }) {
  const { user } = useAuth();
  const [timer, setTimer] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const tickRef = useRef(null);

  const [stopDialogTask, setStopDialogTask] = useState(null);
  const [stopDialogPromise, setStopDialogPromise] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");

  useEffect(() => {
    if (stopDialogTask) {
      setSelectedStatus(stopDialogTask.status);
    } else {
      setSelectedStatus("");
    }
  }, [stopDialogTask]);

  const load = useCallback(async () => {
    if (!user || !user.id) { setTimer(null); return; }
    try {
      const { data } = await api.get("/me/timer/active");
      setTimer(data);
    } catch { setTimer(null); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Poll every 30s in case another tab acts
  useEffect(() => {
    if (!user || !user.id) return;
    pollRef.current = setInterval(load, 30000);
    return () => clearInterval(pollRef.current);
  }, [user, load]);

  // 1s tick to update elapsed display
  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    setElapsed(computeElapsedSeconds(timer));
    if (timer && timer.status === "RUNNING") {
      tickRef.current = setInterval(() => setElapsed(computeElapsedSeconds(timer)), 1000);
    }
    return () => tickRef.current && clearInterval(tickRef.current);
  }, [timer]);

  const start = async (taskId) => {
    try {
      const { data } = await api.post(`/tasks/${taskId}/timer/start`, {});
      setTimer(data);
      await load();
      toast.success("Timer started");
      return data;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const pause = async (taskId) => {
    try {
      const { data } = await api.post(`/tasks/${taskId}/timer/pause`, {});
      setTimer(data);
      toast.success("Timer paused");
      return data;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  };

  const stop = async (taskId) => {
    try {
      const { data: taskDetails } = await api.get(`/tasks/${taskId}`);
      return new Promise((resolve) => {
        setStopDialogTask(taskDetails);
        setStopDialogPromise({ resolve });
      });
    } catch (e) {
      toast.error("Failed to load task details");
      return null;
    }
  };

  const confirmStop = async () => {
    if (!stopDialogTask) return;
    try {
      if (selectedStatus !== stopDialogTask.status) {
        await api.put(`/tasks/${stopDialogTask.id}`, { status: selectedStatus });
      }
      const { data } = await api.post(`/tasks/${stopDialogTask.id}/timer/stop`, {});
      setTimer(null);
      toast.success(`Timer stopped — ${data.accumulated_seconds}s tracked`);
      window.dispatchEvent(new Event("timer-stopped"));
      await load();
      if (stopDialogPromise) {
        stopDialogPromise.resolve(data);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      if (stopDialogPromise) {
        stopDialogPromise.resolve(null);
      }
    } finally {
      setStopDialogTask(null);
      setStopDialogPromise(null);
    }
  };

  const getTaskTimer = async (taskId) => {
    try { const { data } = await api.get(`/tasks/${taskId}/timer`); return data; }
    catch { return null; }
  };

  return (
    <TimerContext.Provider value={{ timer, elapsed, start, pause, stop, refresh: load, getTaskTimer }}>
      {children}

      <Dialog open={!!stopDialogTask} onOpenChange={(open) => {
        if (!open) {
          if (stopDialogPromise) {
            stopDialogPromise.resolve(null);
          }
          setStopDialogTask(null);
          setStopDialogPromise(null);
        }
      }}>
        <DialogContent className="max-w-md w-full whitespace-normal break-words" data-testid="timer-stop-dialog">
          <DialogHeader>
            <DialogTitle>Update Task Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground whitespace-normal break-words">
              You are stopping the timer for task: <strong>{stopDialogTask?.title}</strong>. Please confirm/update the task status:
            </p>
            <div className="space-y-2">
              <Label htmlFor="timer-stop-status-select">Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger id="timer-stop-status-select" data-testid="timer-stop-status-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_STATUSES.map((status) => (
                    <SelectItem key={status} value={status}>
                      {TASK_STATUS_LABELS[status]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (stopDialogPromise) {
                  stopDialogPromise.resolve(null);
                }
                setStopDialogTask(null);
                setStopDialogPromise(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={confirmStop} data-testid="timer-stop-confirm-btn">
              Confirm & Stop
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TimerContext.Provider>
  );
}

export const useTimer = () => useContext(TimerContext);
